/**
 * Cliente HTTP centralizado para hablar con el backend FORJA.
 *
 * Features:
 *  - Inyecta el JWT del localStorage en Authorization.
 *  - Maneja errores en formato estandar { error: { code, message } }.
 *  - Si recibe 401, intenta refresh con el refresh_token y reintenta la
 *    request original. Si el refresh tambien falla, limpia tokens.
 *  - Devuelve el body parseado o tira ApiError con codigo + mensaje.
 */

const ACCESS_TOKEN_KEY = "forja.access_token";
const REFRESH_TOKEN_KEY = "forja.refresh_token";

// Origen del backend. En el build de PRODUCCIÓN se toma de VITE_API_BASE_URL
// (el panel se sirve como estático en otro dominio que el backend, así que
// necesita URLs absolutas). En DEV queda vacío: las rutas relativas `/api/*`
// las intercepta el proxy de Vite (ver vite.config.ts). Ver web/.env.example.
export const API_BASE = import.meta.env.PROD
  ? import.meta.env.VITE_API_BASE_URL ?? ""
  : "";

/**
 * Resuelve la URL de un asset (logo/foto). El backend devuelve rutas RELATIVAS
 * (`/api/v1/assets/{id}`); en producción la web se sirve desde otro dominio que
 * el backend, así que un `<img src="/api/v1/assets/…">` daría 404 (imagen rota).
 * Este helper le antepone el origen del backend. En DEV (API_BASE vacío) queda
 * relativa y la resuelve el proxy de Vite. Ya-absolutas se devuelven tal cual.
 */
export function assetUrl(path?: string | null, ancho?: AnchoDeAsset): string | undefined {
  if (!path) return undefined;
  if (/^(https?:|data:|blob:)/i.test(path)) return path;
  // Una miniatura sólo tiene sentido sobre NUESTROS assets: a una URL de otro
  // origen (ya filtrada arriba) el `?w=` no le dice nada, y a un blob local
  // tampoco. El backend ignora un ancho que no conoce y devuelve el original,
  // así que esto nunca deja a nadie sin imagen.
  const mini = ancho && /\/api\/v1\/assets\//.test(path) ? `${path.includes("?") ? "&" : "?"}w=${ancho}` : "";
  return `${API_BASE}${path}${mini}`;
}

/**
 * Anchos que el backend sabe servir (`assets.miniaturas.ts`). Se pide el DOBLE
 * de lo que mide el hueco en CSS, porque casi todos los teléfonos tienen
 * pantalla 2x: un escudo de 42 px se pide a 96 y se ve nítido igual.
 *
 * Medido el 03-11-2026: el catálogo público bajaba 699 KB de escudos para
 * dibujarlos a 42 px, con un PNG de 336 KB adentro.
 */
export type AnchoDeAsset = 96 | 192 | 384;

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly details?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/**
 * Ruta TEMPLATE: los ids concretos se reemplazan para poder agrupar. Sin esto
 * cada partido sería una fila distinta y "esto falló 12 veces" no existiría.
 */
function rutaTemplate(path: string): string {
  return path
    .split("?")[0]!
    .replace(/\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, "/:id")
    .replace(/\/\d+(?=\/|$)/g, "/:n");
}

/**
 * Avisa que una llamada falló. Va por evento del DOM a propósito: la
 * telemetría importa de este módulo, así que importarla acá cerraría un ciclo.
 * Quien escucha es `installClientErrorReporting()` (ver errorReporting.ts).
 */
function avisarFalloDeApi(detalle: {
  status: number;
  code: string;
  ruta: string;
  metodo: string;
  mensaje: string;
}) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent("forja:api-error", { detail: detalle }));
}

// VER COMO (superusuario): token de sólo lectura que vive en sessionStorage —
// POR PESTAÑA a propósito: la pestaña del panel sigue siendo del admin y sólo
// ésta navega como el usuario. Mientras exista manda sobre la sesión real en
// todo el tab (apiFetch y AuthContext pasan por getAccessToken), así el perfil
// cacheado queda bajo el sub del usuario mirado y no ensucia el del admin.
const VIEW_AS_TOKEN_KEY = "forja.view_as_token";

export function getViewAsToken(): string | null {
  try {
    return sessionStorage.getItem(VIEW_AS_TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setViewAsToken(token: string) {
  sessionStorage.setItem(VIEW_AS_TOKEN_KEY, token);
}

export function clearViewAsToken() {
  try {
    sessionStorage.removeItem(VIEW_AS_TOKEN_KEY);
  } catch {
    // Sin sessionStorage no hay modo ver-como que limpiar.
  }
}

export function getAccessToken(): string | null {
  return getViewAsToken() ?? localStorage.getItem(ACCESS_TOKEN_KEY);
}

export function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
}

export function setSessionTokens(tokens: {
  accessToken: string;
  refreshToken?: string | null;
}) {
  localStorage.setItem(ACCESS_TOKEN_KEY, tokens.accessToken);
  if (tokens.refreshToken) {
    localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refreshToken);
  }
}

export function clearSessionTokens() {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
}

type FetchOptions = {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  /**
   * El OBJETO, no el JSON: apiFetch lo serializa. Un string acá salía
   * codificado dos veces y el backend contestaba 400 — así estuvieron rotas
   * seis escrituras del circuito de la foto oficial (24-09). El tipo deja
   * afuera `string` para que no se pueda repetir.
   */
  body?: object | number | boolean | null;
  /** Si false, NO inyecta el Authorization header (para login, public endpoints). */
  withAuth?: boolean;
  /** Interno: evita loop de refresh dentro del retry. */
  _isRetry?: boolean;
  /**
   * Sólo GET: reusar la última respuesta durante este tiempo (ms). Para lo que
   * cambia por una acción (quién soy, mis pendientes), nunca para lo en vivo.
   * Cualquier escritura exitosa lo invalida (ver apiFetch).
   */
  cacheMs?: number;
  /**
   * Tope de espera. Con señal DÉBIL (no cortada) un fetch puede quedar colgado
   * minutos sin fallar: vencido el tope se corta y se trata como "sin señal".
   * Sólo tiene sentido en pedidos IDEMPOTENTES (con client_event_id): si el
   * server sí lo recibió, el reintento devuelve el mismo evento.
   */
  timeoutMs?: number;
};

// Compartido entre llamadas concurrentes para evitar refreshes paralelos
let refreshPromise: Promise<string | null> | null = null;

async function tryRefresh(): Promise<string | null> {
  if (refreshPromise) return refreshPromise;
  const refreshToken = getRefreshToken();
  if (!refreshToken) return null;

  refreshPromise = (async () => {
    try {
      // Con tope: con señal débil el refresh podía quedar colgado PARA
      // SIEMPRE y con él todo lo que espera un token (la cola de vivo del
      // árbitro quedaba en "Enviando…" sin volver a intentar nunca).
      const corte = new AbortController();
      const reloj = setTimeout(() => corte.abort(), 12000);
      const res = await fetch(`${API_BASE}/api/v1/auth/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refreshToken }),
        signal: corte.signal,
      }).finally(() => clearTimeout(reloj));
      // Sólo un RECHAZO real del server cierra la sesión. Un 5xx es el server
      // con problemas, no el token: cerrarla sacaba al árbitro de la cancha.
      if (!res.ok) {
        if (res.status >= 400 && res.status < 500) clearSessionTokens();
        return null;
      }
      const data = (await res.json()) as { accessToken?: string; refreshToken?: string };
      if (!data.accessToken) {
        clearSessionTokens();
        return null;
      }
      setSessionTokens({
        accessToken: data.accessToken,
        refreshToken: data.refreshToken ?? null,
      });
      return data.accessToken;
    } catch {
      // Sin señal NO es "sesión vencida": se conserva y se reintenta cuando
      // vuelva. Antes un refresh que no llegaba borraba los tokens y el árbitro
      // quedaba deslogueado en pleno partido (el token dura 60', el partido 90).
      return null;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

// Lecturas iguales EN VUELO se comparten. Medido el 24-09-2026: al abrir el
// inicio del jugador salían dos /me/contexts a la vez (el shell y la página
// piden lo mismo al montar), 2 s cada uno contra una base de 5 DTU. Sólo GET
// sin cuerpo, y la clave lleva el token: dos sesiones (o el "ver como") nunca
// comparten respuesta. No es un caché: al terminar el pedido se olvida.
const lecturasEnVuelo = new Map<string, Promise<unknown>>();

// Lecturas que el llamador marcó con `cacheMs` (identidad, pendientes: cosas
// que cambian por una ACCIÓN, no solas). Vive en memoria: recargar la página
// siempre trae todo fresco. CUALQUIER escritura que salga bien lo vacía
// entero — unirse a un equipo, cambiar de liga, editar el perfil —, así lo
// que uno acaba de hacer nunca queda tapado por una respuesta vieja.
const lecturasRecientes = new Map<string, { valor: unknown; vence: number }>();

/**
 * Cuánto se reusa cada familia (decisión de producto, 24-09-2026):
 *  - IDENTIDAD (quién soy, mis ligas y equipos): cambia cuando alguien se une,
 *    cambia de liga o edita su perfil — todo eso es una escritura.
 *  - PENDIENTES (asistencia, favoritos): unos minutos de atraso no le cambian
 *    nada a nadie.
 * Lo EN VIVO (partido en juego, árbitro, chat) no usa esto nunca.
 */
export const IDENTIDAD_MS = 2 * 60_000;
export const PENDIENTES_MS = 3 * 60_000;

/** Para lo que cambia sin pasar por una escritura de esta pestaña (logout, push). */
export function olvidarLecturasRecientes(): void {
  lecturasRecientes.clear();
}

export function apiFetch<T = unknown>(path: string, opts: FetchOptions = {}): Promise<T> {
  const esLectura = (opts.method ?? "GET") === "GET" && opts.body === undefined && !opts._isRetry;
  if (!esLectura) {
    return pedir<T>(path, opts).then((r) => {
      lecturasRecientes.clear();
      return r;
    });
  }
  const clave = `${opts.withAuth === false ? "-" : getAccessToken() ?? ""}|${path}`;
  // Todo el que recibe algo compartido recibe una COPIA: si una pantalla muta
  // lo que le llegó, no le cambia los datos a la otra.
  const copia = (r: T) => (r === undefined || r === null ? r : structuredClone(r));
  if (opts.cacheMs) {
    const guardado = lecturasRecientes.get(clave);
    if (guardado && guardado.vence > Date.now()) return Promise.resolve(copia(guardado.valor as T));
  }
  const enVuelo = lecturasEnVuelo.get(clave) as Promise<T> | undefined;
  if (enVuelo) return enVuelo.then(copia);
  const pedido = pedir<T>(path, opts)
    .then((r) => {
      if (opts.cacheMs) lecturasRecientes.set(clave, { valor: copia(r), vence: Date.now() + opts.cacheMs });
      return r;
    })
    .finally(() => lecturasEnVuelo.delete(clave));
  lecturasEnVuelo.set(clave, pedido);
  return pedido;
}

async function pedir<T>(path: string, opts: FetchOptions): Promise<T> {
  // Identifica al cliente en el Log de errores del panel (ver errorLog.ts).
  const headers: Record<string, string> = { "X-Forja-Client": `web/${__FORJA_BUILD__}` };

  const withAuth = opts.withAuth !== false;
  if (withAuth) {
    let token = getAccessToken();
    // Si otra pestaña cerró la sesión, el token puede desaparecer antes de
    // que esta pestaña actualice su estado de React. Intentamos recuperar la
    // sesión antes de enviar una petición sin Authorization.
    if (!token && !opts._isRetry) {
      token = await tryRefresh();
      if (!token) {
        if (typeof window !== "undefined") {
          window.dispatchEvent(new Event("forja:auth-expired"));
        }
        throw new ApiError(401, "missing_token", "La sesión expiró. Volvé a iniciar sesión.");
      }
    }
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }
  }

  const init: RequestInit = {
    method: opts.method ?? "GET",
    headers,
  };
  // El Content-Type sólo va cuando hay body. Si lo mandamos en un request sin
  // cuerpo (DELETE, GET), Fastify lo rechaza con "Body cannot be empty when
  // content-type is set to 'application/json'".
  if (opts.body !== undefined) {
    headers["Content-Type"] = "application/json";
    init.body = JSON.stringify(opts.body);
  }

  let res: Response;
  const corte = opts.timeoutMs ? new AbortController() : null;
  const reloj = corte ? setTimeout(() => corte.abort(), opts.timeoutMs) : null;
  if (corte) init.signal = corte.signal;
  try {
    res = await fetch(`${API_BASE}${path}`, init);
  } catch (fallo) {
    // La red no respondió: sin señal, DNS caído, servidor inalcanzable. Es el
    // error que más sufre el que carga desde la cancha y el que NUNCA llegaba
    // a ningún lado (no hay request que el backend pueda registrar).
    avisarFalloDeApi({
      status: 0,
      code: "sin_red",
      ruta: rutaTemplate(path),
      metodo: opts.method ?? "GET",
      mensaje: fallo instanceof Error ? fallo.message : String(fallo),
    });
    // Se re-lanza como ApiError con un mensaje HUMANO, no el crudo del
    // navegador. Cargando una fecha desde la cancha, lo que se veía era
    // "Failed to fetch (forja-backend-wdm3.onrender.com)" — en inglés y con el
    // hostname de Render adentro (probado con Modo Avión, 26-08-2026). Eso no
    // le dice al organizador ni que fue la señal, ni —lo más importante— que lo
    // que cargó NO se perdió: el borrador queda en el teléfono.
    throw new ApiError(
      0,
      "sin_red",
      "Sin señal. Lo que cargaste queda guardado acá: probá de nuevo cuando vuelva.",
      { causa: fallo instanceof Error ? fallo.message : String(fallo) },
    );
  }

  let parsed: unknown;
  let text: string;
  try {
    // El tope cubre TAMBIÉN el cuerpo: con señal débil los encabezados llegan
    // y el cuerpo se queda colgado.
    text = await res.text();
  } catch (fallo) {
    throw new ApiError(0, "sin_red", "Sin señal. Lo que cargaste queda guardado acá: probá de nuevo cuando vuelva.", {
      causa: fallo instanceof Error ? fallo.message : String(fallo),
    });
  } finally {
    if (reloj) clearTimeout(reloj);
  }
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = text;
    }
  }

  if (!res.ok) {
    const errPayload = (parsed as { error?: { code?: string; message?: string; details?: unknown } } | undefined)?.error;
    const code = errPayload?.code ?? `http_${res.status}`;
    const message = errPayload?.message ?? `HTTP ${res.status}`;

    // 401 → intentar refresh + retry (una sola vez, y solo si withAuth)
    if (res.status === 401 && withAuth && !opts._isRetry && !path.includes("/auth/")) {
      // La sesión VER COMO no tiene refresh a propósito: vencida se limpia y
      // se avisa — refrescar acá reviviría la pestaña como el ADMIN, y una
      // pantalla que dice ser de otro pero muestra tus datos es peor que morir.
      if (getViewAsToken()) {
        clearViewAsToken();
        if (typeof window !== "undefined") {
          window.dispatchEvent(new Event("forja:view-as-expired"));
        }
        throw new ApiError(401, "view_as_expired", "La sesión VER COMO venció. Cerrá esta pestaña.");
      }
      const newToken = await tryRefresh();
      if (newToken) {
        return apiFetch<T>(path, { ...opts, _isRetry: true });
      }
      // Refresh fallido: tokens ya limpios por tryRefresh.
    }

    avisarFalloDeApi({
      status: res.status,
      code,
      ruta: rutaTemplate(path),
      metodo: opts.method ?? "GET",
      mensaje: message,
    });
    throw new ApiError(res.status, code, message, errPayload?.details);
  }

  return parsed as T;
}

/**
 * Un ARCHIVO del servidor (PDF, Excel) con la sesión del usuario: `apiFetch`
 * espera JSON. Devuelve el contenido y el nombre que manda el servidor en
 * Content-Disposition. Si el token venció, se renueva una vez y se reintenta.
 */
export async function apiArchivo(path: string): Promise<{ blob: Blob; nombre: string }> {
  const pedirArchivo = (token: string | null) =>
    fetch(`${API_BASE}${path}`, {
      headers: { "X-Forja-Client": `web/${__FORJA_BUILD__}`, ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    });
  let res: Response;
  try {
    res = await pedirArchivo(getAccessToken() ?? (await tryRefresh()));
    if (res.status === 401) {
      const nuevo = await tryRefresh();
      if (nuevo) res = await pedirArchivo(nuevo);
    }
  } catch {
    throw new ApiError(0, "sin_red", "Sin señal. Probá de nuevo cuando vuelva.");
  }
  if (!res.ok) {
    let error: { code?: string; message?: string } | undefined;
    try { error = ((await res.json()) as { error?: { code?: string; message?: string } }).error; } catch { /* no era JSON */ }
    throw new ApiError(res.status, error?.code ?? `http_${res.status}`, error?.message ?? `HTTP ${res.status}`);
  }
  const nombre = /filename="?([^";]+)"?/.exec(res.headers.get("Content-Disposition") ?? "")?.[1] ?? "archivo";
  return { blob: await res.blob(), nombre };
}
